import React, {
  useEffect,
  useRef,
  useState,
  FunctionComponent,
  SyntheticEvent,
} from 'react';

import classnames from 'classnames';
import { trimProps } from '@components/library/helper';
import { dropdownKeydownHelper } from '@components/library/dropdown/dropdown_helper';
import { ListDropdownItem } from '@components/library/dropdown_item/index';
import {
  DropdownNavigateKeys,
  ListDropdownTransitions,
  ListOpenDirections,
  ListOption,
  ListOptionValue,
  UserDevices,
} from '@components/library/dropdown/index';
import BaseDropdown from '../BaseDropdown';

import styleClassNames from './styles.module.scss';

interface ListDropdownProps {
  anchorElement?: JSX.Element;
  anchorElementLabel?: string;
  className?: string;
  dropdownTransition?: ListDropdownTransitions;
  dropdownTransitionTime?: number;
  onChange?: (listOption: ListOption, e?: SyntheticEvent) => void;
  openDirection?: ListOpenDirections;
  openOnClick?: boolean;
  openOnHover?: boolean;
  options?: ListOption[];
  placeholder?: string;
  searchable?: boolean;
  userDevice?: UserDevices;
  value?: ListOption | ListOptionValue;
}

// props not needed in base component
const propsToRemove = 'onChange,options,placeholder,value';

export const ListDropdown: FunctionComponent<ListDropdownProps> = (
  props
) => {
  const {
    anchorElement,
    anchorElementLabel,
    dropdownTransition = ListDropdownTransitions.FadeIn,
    dropdownTransitionTime = 300,
    onChange,
    openDirection = ListOpenDirections.Down,
    options,
    placeholder = t('Select value...'),
    searchable = false,
    value: optionValue, // renaming for readability
  } = props;
  const [closeDropdown, setCloseDropdown] = useState(false); // callback used by variant to trigger closing the dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // tracks open/closed status of dropdown
  const [searchInput, setSearchInput] = useState('');
  const [arrowedItem, setArrowedItem] = useState<ListOptionValue>(null);
  const searchInputRef = useRef(null);

  const selectedValue = (optionValue as ListOption)?.value ?? optionValue; // allows ListOptionValue or ListOption passed in
  const selectedLabel = options.find((option) => option.value === selectedValue)?.label;

  const defaultAnchorElement = (
    <div
      className={classnames(
        'anchor-element',
        styleClassNames['anchor-element'],
        { [styleClassNames['placeholder-shown']]: (selectedLabel === undefined || selectedLabel === null) },
      )}
    >
      {anchorElementLabel && (
        <span className={classnames("anchor-element-label", styleClassNames['anchor-element-label'])}>
          {anchorElementLabel}
        </span>
      )}
      {(!searchInput || !isDropdownOpen) && (
        <span className={classnames("selected-option", styleClassNames['selected-option'])}>
          {selectedLabel ?? placeholder}
        </span>
      )}
      {searchable && isDropdownOpen && (
        <input
          className={`search-input ${styleClassNames['search-input']}`}
          data-testid="searchable-dropdown-input"
          onChange={(e: InputChangeEvent): void => setSearchInput(e.target.value)}
          ref={searchInputRef}
          value={searchInput}
        />
      )}
      <span className={`bff-caret-down ${styleClassNames['anchor-icon']}`} />
    </div>
  );

  const searchedOptions = options.filter((option) => (
    option.label?.toString().toLowerCase().includes(searchInput?.toLowerCase())
  ));

  const handleOnChange = (selectedOptionValue: ListOptionValue): void => {
    onChange(options.find((option) => option.value === selectedOptionValue));
    setCloseDropdown(true);
  };

  const handleKeydown = (key: DropdownNavigateKeys): void => {
    const targetedIndex = searchedOptions?.findIndex((option) => option.value === arrowedItem);
    const newIndex = dropdownKeydownHelper(key, searchedOptions?.length, targetedIndex);

    if (typeof newIndex === 'number') {
      setArrowedItem(searchedOptions[newIndex].value);
    } else if (newIndex) {
      handleOnChange(arrowedItem);
    }
  };

  const noOptionsOption = { label: t('No Options'), value: undefined, isDisabled: true };
  const filteredOptions = (): ListOption[] => {
    if (searchable) {
      return searchedOptions?.length ? [...searchedOptions] : [noOptionsOption];
    }

    return options;
  };

  const optionsAsItems = filteredOptions().map((option, i) => (
    <ListDropdownItem
      className={classnames({ selected: selectedValue === option.value, arrowed: arrowedItem === option.value })}
      id={option.value?.toString()}
      isArrowed={arrowedItem === option.value}
      isDisabled={option.isDisabled}
      isSelected={selectedValue === option.value}
      isTabbable={false}
      key={`${i}${option.value}`}
      name={option.value}
      onChoose={handleOnChange}
    >
      {option.label}
    </ListDropdownItem>
  ));

  useEffect(() => {
    if (isDropdownOpen && searchable) {
      searchInputRef.current.focus();
    }

    if (!isDropdownOpen) {
      setArrowedItem(null);

      if (searchable) {
        setTimeout(() => {
          setSearchInput('');
        }, dropdownTransitionTime + 50); // setTimeout ensures search input isn't cleared until after dropdown closes (otherwise dropdown will jump back to unsearched state)
      }
    }
  }, [isDropdownOpen]);

  const baseProps = {
    ...trimProps(props, propsToRemove),
    anchorElement: anchorElement || defaultAnchorElement,
    arrowedItem,
    children: optionsAsItems,
    closeCallback: (): void => setCloseDropdown(false),
    closeDropdown,
    dropdownTransition,
    dropdownTransitionTime,
    keydownCallback: handleKeydown,
    openDirection,
    setIsDropdownOpen,
    styleClassNames,
    useUlContainer: true,
    variantName: 'list',
  };

  return <BaseDropdown {...baseProps} />;
};
